/**
 * Centralized route path definitions with type-safe template functions.
 * 
 * Usage:
 *   import { routes } from '@/lib/routing';
 *   navigate(routes.device({ deviceId: '123' }));
 *   navigate(routes.customerSite({ customerId: '1', siteId: '2' }));
 */

// Route parameter interfaces
export interface CustomerParams {
  customerId: string;
}

export interface SiteParams {
  customerId: string;
  siteId: string;
}

export interface DeviceParams {
  deviceId: string;
}

export interface CameraParams {
  deviceId: string;
  cameraId: string;
}

export interface AlertParams {
  alertId: string;
}

export interface UserParams {
  userId: string;
}

export interface TeamParams {
  teamId: string;
}

export interface ConnectionParams {
  connectionId: string;
}

export interface IntegrationParams {
  integrationId: string;
}

// Route definitions with typed parameters
export const routes = {
  // Dashboard & Home
  home: () => '/' as const,
  dashboard: () => '/dashboard' as const,
  
  // Customers
  customers: () => '/customers' as const,
  customer: ({ customerId }: CustomerParams) => `/customers/${customerId}` as const,
  
  // Sites
  sites: () => '/sites' as const,
  siteNew: () => '/site/new' as const,
  customerSite: ({ customerId, siteId }: SiteParams) => 
    `/Customer/${customerId}/project/${siteId}` as const,
  customerSiteSettings: ({ customerId, siteId }: SiteParams) => 
    `/Customer/${customerId}/project/${siteId}/settings` as const,
  customerSiteCalendar: ({ customerId, siteId }: SiteParams) => 
    `/Customer/${customerId}/project/${siteId}/calendar` as const,
  customerSiteUsers: ({ customerId, siteId }: SiteParams) => 
    `/Customer/${customerId}/project/${siteId}/users` as const,
  
  // Devices
  devices: () => '/devices' as const,
  device: ({ deviceId }: DeviceParams) => `/devices/${deviceId}` as const,
  deviceEdit: ({ deviceId }: DeviceParams) => `/devices/${deviceId}/edit` as const,
  deviceCamera: ({ deviceId, cameraId }: CameraParams) => 
    `/devices/${deviceId}/camera/${cameraId}` as const,
  deviceAdd: () => '/devices/add-device' as const,
  deviceAddForCustomer: ({ customerId }: CustomerParams) => 
    `/devices/customer/${customerId}/add-device` as const,
  
  // Alerts
  alerts: () => '/alerts' as const,
  alert: ({ alertId }: AlertParams) => `/alert/${alertId}` as const,
  alertAudit: () => '/alert-audit' as const,
  
  // Users
  users: () => '/users' as const,
  userAdd: () => '/users/add' as const,
  userEdit: ({ userId }: UserParams) => `/users/${userId}` as const,
  
  // Teams
  teams: () => '/teams' as const,
  teamAdd: () => '/team/add' as const,
  team: ({ teamId }: TeamParams) => `/team/${teamId}` as const,
  
  // Connections
  connections: () => '/connections' as const,
  connectionAdd: () => '/connections/add-connection' as const,
  connectionEdit: ({ connectionId }: ConnectionParams) => 
    `/connections/${connectionId}` as const,
  
  // Integrations
  integrations: () => '/integrations' as const,
  integration: ({ integrationId }: IntegrationParams) => 
    `/integrations/${integrationId}` as const,
  
  // Reporting
  reporting: () => '/reporting' as const,
  
  // Viewer
  viewer: () => '/viewer' as const,
  
  // Map
  map: () => '/map' as const,
  
  // System / Ingestion
  system: () => '/system' as const,
  ingestion: () => '/ingestion' as const,
  inbound: () => '/ingestion/legacy' as const,
  
  // Auth
  login: () => '/login' as const,
  forgotPassword: () => '/forgot-password' as const,
  createPassword: () => '/create-password' as const,
} as const;

// Type for all route keys
export type RouteKey = keyof typeof routes;

// Extract the return type of a route function
export type RoutePath<K extends RouteKey> = ReturnType<typeof routes[K]>;
